import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Bell, CheckCheck, Loader2 } from 'lucide-react';
import { getNotifications, markNotificationsRead } from '../../config';
import { useAuth } from '../../context/AuthContext';

/**
 * Cloche des alertes du back-office.
 *
 * Le serveur écrit des alertes depuis longtemps — anomalies de paiement
 * (`record_payment_anomaly` : paiement encaissé sur une commande morte, ligne payée
 * que la boutique ne peut pas honorer) et conflits catalogue
 * (`record_catalog_conflict` : ligne de panier encore revendiquée après un geste
 * catalogue). Aucune n'avait d'écran : `getNotifications` et `markNotificationsRead`
 * étaient exportés sans être importés nulle part, et `unread_notifications`, pourtant
 * renvoyé par le tableau de bord, n'apparaissait dans aucun fichier du front. Un
 * mécanisme d'alerte sans écran ne sert à rien : la promesse de l'article 6 des CGV
 * (informer et rembourser sans délai) reposait sur des documents que personne ne
 * voyait.
 *
 * POURQUOI DANS L'EN-TÊTE, ET PAS UNE PAGE DÉDIÉE. Une alerte naît d'un geste fait
 * ailleurs — un paiement encaissé pendant qu'on est sur les réservations, un conflit
 * né d'un geste sur le catalogue. Elle doit donc être visible depuis TOUTES les pages
 * d'administration, ce que seul l'en-tête de `AdminLayout` permet. Une page dédiée
 * n'aurait été consultée que par qui la cherche, c'est-à-dire par qui sait déjà. Le
 * tableau de bord, lui, ne montre que des compteurs sans détail ni geste possible.
 *
 * Le compteur vient de la LISTE, et non de `stats.unread_notifications` du tableau de
 * bord : une seule lecture, donc jamais un compteur qui contredit ce que le panneau
 * affiche. `GET /api/admin/notifications` plafonne à 50 documents, ce qui borne aussi
 * la pastille — largement au-dessus du volume qu'un gestionnaire laisse s'accumuler.
 *
 * Rappel du piège maison : un `ObjectId` Mongo brut dans une charge utile d'alerte
 * fait échouer l'encodage JSON de la réponse ENTIÈRE, donc tombe toute la cloche et
 * pas seulement l'alerte fautive. Le nettoyage est centralisé côté serveur dans
 * `_record_back_office_alert` ; toute nouvelle famille d'alerte doit y passer.
 */

const LIBELLES_TYPE = {
  payment_anomaly: 'Anomalie de paiement',
  catalog_conflict: 'Conflit catalogue',
  reservation: 'Nouvelle réservation',
  reservation_ready: 'Réservation prête',
  special_order_paid: 'Acompte de commande payé',
};

// Les alertes qui appellent un geste humain immédiat (remboursement, correction du
// catalogue) se distinguent des simples traces d'activité.
const TYPES_CRITIQUES = new Set(['payment_anomaly', 'catalog_conflict']);

function formatDate(valeur) {
  if (!valeur) return '';
  try {
    return new Date(valeur).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '';
  }
}

/**
 * Texte de l'alerte. Les familles récentes portent un `message` rédigé côté serveur,
 * qui dit déjà quoi faire. Les trois familles historiques n'en ont pas : on reconstitue
 * l'essentiel depuis le produit et le client plutôt que d'afficher une ligne vide.
 */
function resume(alerte) {
  if (alerte?.message) return alerte.message;
  const produit = alerte?.product?.name || alerte?.reservation?.product_name || '';
  const client = [alerte?.reservation?.first_name, alerte?.reservation?.last_name]
    .filter(Boolean)
    .join(' ');
  const morceaux = [produit, client].filter(Boolean);
  return morceaux.length ? morceaux.join(' — ') : 'Aucun détail transmis.';
}

export default function NotificationsBell() {
  const { hasPermission } = useAuth();
  // Même permission que l'endpoint : une cloche qui répondrait 403 à chaque ouverture
  // n'aiderait personne.
  const peutVoir = hasPermission('dashboard:view');

  const [alertes, setAlertes] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(false);
  const [marquage, setMarquage] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const reponse = await getNotifications();
      setAlertes(Array.isArray(reponse?.notifications) ? reponse.notifications : []);
      setErreur(false);
    } catch {
      // Best-effort strict, comme côté serveur : une cloche en panne ne doit jamais
      // empêcher de travailler. On le dit, on ne casse rien.
      setErreur(true);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (peutVoir) charger();
  }, [peutVoir, charger]);

  if (!peutVoir) return null;

  const nonLues = alertes.filter((alerte) => !alerte?.read).length;

  const basculer = () => {
    const prochain = !ouvert;
    setOuvert(prochain);
    // Relecture à l'ouverture : un gestionnaire reste des heures sur la même page,
    // sans quoi la cloche n'annoncerait que les alertes présentes à son arrivée.
    if (prochain) charger();
  };

  const marquerLues = async () => {
    setMarquage(true);
    try {
      await markNotificationsRead();
      // « Lu » n'est pas « effacé » : les alertes restent consultables, seule la
      // sollicitation disparaît.
      setAlertes((precedentes) => precedentes.map((alerte) => ({ ...alerte, read: true })));
    } catch {
      setErreur(true);
    } finally {
      setMarquage(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={basculer}
        className="relative p-2 rounded-sm border border-slate-200 hover:bg-slate-100 transition-colors"
        data-testid="admin-notifications-toggle"
        aria-label={nonLues > 0 ? `Alertes — ${nonLues} non lue(s)` : 'Alertes'}
        aria-expanded={ouvert}
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {nonLues > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="admin-notifications-badge"
          >
            {nonLues}
          </span>
        )}
      </button>

      {ouvert && (
        <>
          {/* Fermeture au clic à côté : un panneau qui ne se referme qu'au bouton
              finit toujours par masquer la page qu'on veut lire. */}
          <div className="fixed inset-0 z-40" onClick={() => setOuvert(false)} aria-hidden="true" />
          <div
            className="absolute right-0 mt-2 w-[min(26rem,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto z-50 bg-white border border-slate-200 rounded-sm shadow-xl"
            data-testid="admin-notifications-panel"
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 sticky top-0 bg-white">
              <span className="font-heading text-sm uppercase tracking-tight text-primary">
                Alertes
              </span>
              {nonLues > 0 && (
                <button
                  type="button"
                  onClick={marquerLues}
                  disabled={marquage}
                  className="inline-flex items-center gap-1.5 font-accent text-[10px] uppercase tracking-widest text-slate-600 hover:text-primary transition-colors disabled:opacity-50"
                  data-testid="admin-notifications-mark-read"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Tout marquer comme lu
                </button>
              )}
            </div>

            {chargement && alertes.length === 0 && !erreur && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-secondary animate-spin" />
              </div>
            )}

            {erreur && (
              <p className="px-4 py-6 text-sm text-rose-700" data-testid="admin-notifications-error">
                Impossible de charger les alertes pour le moment. Le reste du back-office
                fonctionne normalement — réessayez en rouvrant la cloche.
              </p>
            )}

            {!erreur && !chargement && alertes.length === 0 && (
              <p className="px-4 py-6 text-sm text-slate-600" data-testid="admin-notifications-empty">
                Aucune alerte pour le moment.
              </p>
            )}

            <ul className="divide-y divide-slate-100">
              {alertes.map((alerte, index) => (
                <li
                  key={`${alerte?.type || 'alerte'}-${alerte?.created_at || index}-${index}`}
                  className={`px-4 py-3 ${alerte?.read ? 'bg-white' : 'bg-secondary/5'}`}
                  data-testid={`admin-notification-${index}`}
                  data-lu={alerte?.read ? 'true' : 'false'}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span
                      className={`inline-flex items-center gap-1.5 font-accent text-[10px] uppercase tracking-widest ${
                        TYPES_CRITIQUES.has(alerte?.type) ? 'text-rose-700' : 'text-slate-500'
                      }`}
                    >
                      {TYPES_CRITIQUES.has(alerte?.type) && <AlertTriangle className="w-3.5 h-3.5" />}
                      {LIBELLES_TYPE[alerte?.type] || alerte?.type || 'Alerte'}
                    </span>
                    <span className="text-[11px] text-slate-500 shrink-0">
                      {formatDate(alerte?.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">{resume(alerte)}</p>
                  {alerte?.reservation?.reservation_id && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      Commande {alerte.reservation.reservation_id}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
